import React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import {
  FormCaption,
  FormContainer,
  InformSpan,
  SixDigitInput
} from '../../common/components';
import { ROUTER_LINKS } from '../../common/consts/app-keys.const';
import { APP_KEYS } from '../../common/consts';
import { authService } from '../service/auth.service';
import { TOAST_SEND_CODE_CONTENT } from '../consts';
import { useThrottle } from '../../hooks/useThrottle';
import { ErrorIcon } from '../../common/components/icons/error-icon.component';
import { useCodeRegister } from '../../hooks/codeRegister';

interface Inputs {
  code: string;
}

export const PasswordRecovery = () => {
  const { t } = useTranslation();
  const [serverError, setServerError] = React.useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const email = (location.state as { email?: string })?.email;

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm<Inputs>();

  const { mutate: sendRecoveryCode } = useMutation({
    mutationKey: [APP_KEYS.QUERY_KEYS.RECOVERY_CODE],
    mutationFn: (data: Inputs) => {
      return authService.checkRecoveryCode({ token: data.code });
    },
    onSuccess: (token) => {
      navigate(`${ROUTER_LINKS.CREATE_NEW_PASSWORD}/${token}`, {
        state: { email }
      });
    },
    onError: (err: any) => {
      if (err.response?.data?.message === 'Invalid token') {
        setServerError(t('auth.codeIncorrect'));
      }
    }
  });

  const throttledSendRecoveryCode = useThrottle(sendRecoveryCode);

  const { mutate: resendCode } = useMutation({
    mutationKey: [APP_KEYS.QUERY_KEYS.RECOVERY_CODE],
    mutationFn: (email: string) => authService.resendRecoveryCode({ email }),
    onSuccess: () => {
      toast.success(TOAST_SEND_CODE_CONTENT.text);
    },
    onError: () => {
      toast.error(t('auth.resendFailed'));
    }
  });

  const throttledResendCode = useThrottle(resendCode);

  const {
    codeRegister,
    resendCodeHandler,
    onSubmit,
    formattedValue,
    setFormattedValue
  } = useCodeRegister({
    register,
    throttledResendCode,
    throttledSubmit: throttledSendRecoveryCode,
    handleSubmit,
    setValue,
    email: email ?? undefined
  });

  const positionStyles = errors.code ? 'bottom-14' : 'bottom-5';

  React.useEffect(() => {
    if (serverError && formattedValue.length < 6) {
      setServerError('');
    }
  }, [formattedValue]);

  return (
    <div className={`relative ${serverError ? 'pt-[22px] md:pt-[32px]' : ''}`}>
      {serverError && (
        <div className="absolute flex justify-center gap-[8px] items-center top-[-22px] md:top-[-32px] left-[-22px] md:left-[-32px] w-[345px] md:w-[456px] h-[32px] bg-red-500 text-surface-base text-center rounded-t-[42px] py-[6px] text-surface-base font-body text-[14px] font-regular">
          <ErrorIcon />
          {serverError}
        </div>
      )}

      <FormCaption>{t('auth.recoveryTitle')}</FormCaption>

      <InformSpan>
        {t('auth.enterResetCode')} {email ?? ''}
        <Link
          to={ROUTER_LINKS.FORGOT_PASSWORD}
          className="text-base-black ml-[10px] font-medium hover:text-blue-500"
        >
          {t('auth.change')}
        </Link>
      </InformSpan>

      <FormContainer onSubmit={handleSubmit(onSubmit)}>
        <SixDigitInput
          errors={errors}
          name="code"
          setFormattedValue={setFormattedValue}
          formattedValue={formattedValue}
          register={codeRegister}
        >
          <div className="flex gap-[10px] justify-center text-center my-[24px]">
            <p className="text-[16px] font-regular font-body text-gray-900">
              {t('auth.dontSeeCode')}
            </p>
            <div
              className={`flex right-4 ${positionStyles} cursor-pointer`}
              onClick={resendCodeHandler}
            >
              <span className="text-[16px] font-medium font-body text-base-black hover:text-blue-600">
                {t('auth.resendCode')}
              </span>
            </div>
          </div>
        </SixDigitInput>
      </FormContainer>
    </div>
  );
};
